// MemberAdhesion.jsx — Page /espace/adhesion
// Permet à un membre de :
//   - voir la tarification proposée par son asso
//   - voir ses adhésions en cours (active / en_attente_paiement)
//   - souscrire à une formule (free_first / per_meeting / annual_1x / annual_3x)
//   - Stripe = MOCKED (URL MOCK://...) tant que les clés ne sont pas fournies
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Gift, Coins, Sparkles, Loader2, CheckCircle2, Clock, X, FileText, Repeat, AlertCircle,
} from 'lucide-react';

export default function MemberAdhesion() {
  const { asso } = useOutletContext();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tarif, setTarif] = useState(null);
  const [adhesions, setAdhesions] = useState([]);
  const [recus, setRecus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [selectedFormule, setSelectedFormule] = useState(null);
  const stripeConfigured = !!asso?.integrations?.stripe?.configured;

  const load = async () => {
    setLoading(true);
    try {
      const [t, a, r] = await Promise.all([
        api.get('/adhesions-ref/tarification'),
        api.get('/adhesions-ref/me'),
        api.get('/adhesions-ref/recus').catch(() => ({ data: [] })),
      ]);
      setTarif(t.data); setAdhesions(a.data); setRecus(r.data || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadRecu = async (recuId, numero) => {
    try {
      const res = await api.get(`/adhesions-ref/recu/${recuId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url; link.download = `recu-${numero}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Reçu indisponible'); }
  };

  const clearParams = () => setParams({}, { replace: true });

  // Retour depuis Stripe Checkout : on vérifie l'encaissement (fallback webhook)
  useEffect(() => {
    const paiement = params.get('paiement');
    const sid = params.get('session_id');
    if (paiement === 'success' && sid) {
      setVerifying(true);
      let tries = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/adhesions-ref/paiement/status/${sid}`);
          if (data.payment_status === 'paid') {
            const adh = data.adhesion;
            toast.success(adh?.statut === 'active'
              ? `Paiement confirmé — adhésion active${adh?.numero_adherent ? ` · N° ${adh.numero_adherent}` : ''}`
              : 'Paiement reçu — échéance enregistrée');
            setVerifying(false); clearParams(); load();
            return;
          }
        } catch { /* transient */ }
        tries += 1;
        if (tries < 10) setTimeout(poll, 2000);
        else { setVerifying(false); toast.info('Paiement en cours de vérification…'); clearParams(); load(); }
      };
      poll();
    } else if (paiement === 'cancel') {
      toast.info('Paiement annulé'); clearParams();
    }
  }, []);

  const payOnline = async (id) => {
    try {
      const { data } = await api.post(`/adhesions-ref/me/${id}/checkout`, { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error('Lien de paiement indisponible');
    } catch (err) { toast.error(err?.response?.data?.detail || err?.response?.data?.error || 'Erreur'); }
  };

  const STATUT_LABEL = {
    en_attente_validation: 'En attente de validation',
    validee_attente_paiement: 'Validée · à régler',
    en_cours_paiement: 'Paiement partiel',
    active: 'Active', refusee: 'Refusée',
  };

  if (loading) return <div className="p-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;

  const aDejaUtiliseFreeFirst = adhesions.length > 0;
  const adhActive = adhesions.find((a) => a.statut === 'active');

  const formules = [
    { key: 'free_first', icon: Gift, title: 'Découverte', subtitle: '1ère réunion offerte', disabled: aDejaUtiliseFreeFirst,
      perks: ['1 réunion incluse', 'Présentation du club', 'Pas d\'engagement'] },
    { key: 'per_meeting', icon: Coins, title: 'À la réunion', subtitle: 'paiement à la séance',
      perks: ['Réservation à l\'unité', 'Repas inclus', 'Idéal pour tester'] },
    { key: 'annual_1x', icon: Sparkles, title: 'Annuel', subtitle: 'paiement en 1 fois', highlighted: true,
      perks: ['Toutes les réunions', 'Annuaire + messages + gazette', 'Repas inclus à chaque RDV'] },
    { key: 'annual_3x', icon: Sparkles, title: 'Annuel 3×', subtitle: '3 mensualités sans frais',
      perks: ['Toutes les réunions', '3× sans frais via Stripe', 'Annuaire + messages + gazette'] },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="adhesion-page">
      <div className="mb-6 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Adhésion</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">Mon adhésion</h1>
        <p className="text-sm text-[#475569] mt-2">Choisissez la formule qui vous convient pour profiter pleinement de {asso.nom}.</p>
      </div>

      {verifying && (
        <div className="card mb-4 flex items-center gap-3" data-testid="adhesion-verifying">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--theme-primary)' }} />
          <span className="text-sm text-[#0F172A]">Vérification de votre paiement…</span>
        </div>
      )}

      {/* Statut actuel */}
      {adhActive && (
        <div className="card !bg-emerald-50/50 !border-emerald-200 mb-6" data-testid="adhesion-active-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500 text-white grid place-items-center"><CheckCircle2 className="w-5 h-5" /></div>
            <div>
              <div className="font-bold text-emerald-900">Adhésion active</div>
              <div className="text-sm text-emerald-800">
                {adhActive.label}{adhActive.date_fin ? ` · jusqu'au ${new Date(adhActive.date_fin).toLocaleDateString('fr-FR')}` : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formules */}
      <h2 className="font-display text-lg font-bold text-[#0F172A] mb-3">Formules disponibles</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {formules.map((f) => {
          const t = tarif?.[f.key];
          if (!t) return null;
          return (
            <FormuleCard
              key={f.key}
              icon={f.icon}
              title={f.title}
              subtitle={f.subtitle}
              prix_cts={t.prix_cts}
              count={t.count}
              perks={f.perks}
              highlighted={f.highlighted}
              disabled={f.disabled}
              onClick={() => setSelectedFormule(f.key)}
              testid={`formule-${f.key}`}
            />
          );
        })}
      </div>

      {/* Historique */}
      <h2 className="font-display text-lg font-bold text-[#0F172A] mb-3">Mon historique</h2>
      {adhesions.length === 0 ? (
        <div className="card text-center py-12 text-sm text-[#475569]" data-testid="adhesion-history-empty">
          Aucune adhésion encore. Choisissez une formule ci-dessus pour démarrer.
        </div>
      ) : (
        <div className="space-y-2">
          {adhesions.map((a) => {
            const canPay = ['validee_attente_paiement', 'en_cours_paiement'].includes(a.statut);
            return (
              <div key={a.id} className="card flex items-center gap-3 flex-wrap" data-testid={`adhesion-row-${a.id}`}>
                <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${a.statut === 'active' ? 'bg-emerald-100 text-emerald-700' : a.statut === 'refusee' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                  {a.statut === 'active' ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0F172A] text-sm truncate">{a.label}</div>
                  <div className="text-xs text-[#475569]">
                    Souscrit le {new Date(a.created_at).toLocaleDateString('fr-FR')}
                    {a.date_fin && ` · valide jusqu'au ${new Date(a.date_fin).toLocaleDateString('fr-FR')}`}
                    {a.numero_adherent && ` · N° ${a.numero_adherent}`}
                  </div>
                </div>
                {canPay && stripeConfigured && (
                  <button onClick={() => payOnline(a.id)} className="btn-primary text-xs" data-testid={`pay-${a.id}`}>
                    Payer en ligne (CB){a.formule === 'annual_3x' ? ` · échéance ${(a.paiements?.length || 0) + 1}/3` : ''}
                  </button>
                )}
                {canPay && !stripeConfigured && (
                  <span className="text-[11px] text-[#94A3B8] font-medium" data-testid={`pay-offline-${a.id}`}>
                    Règlement auprès du bureau
                  </span>
                )}
                <div className="text-right shrink-0">
                  <div className="font-display font-black" style={{ color: 'var(--theme-primary)' }}>
                    {(a.prix_cts / 100).toFixed(2)} €
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">
                    {STATUT_LABEL[a.statut] || a.statut.replace('_', ' ')}
                  </div>
                </div>
                {(() => {
                  const rs = recus.filter((r) => r.adhesion_id === a.id);
                  const nextAuto = (a.echeances_auto || []).find((e) => e.status === 'scheduled');
                  const failed = (a.echeances_auto || []).find((e) => e.status === 'needs_action');
                  if (!rs.length && !nextAuto && !failed) return null;
                  return (
                    <div className="w-full flex flex-wrap items-center gap-x-4 gap-y-2 pt-3 mt-1 border-t border-[#F1F5F9]">
                      {failed && (
                        <span className="text-[11px] text-rose-600 font-bold flex items-center gap-1" data-testid={`echeance-echec-${a.id}`}>
                          <AlertCircle className="w-3 h-3" /> Prélèvement échoué — régularisez avec le bouton « Payer en ligne »
                        </span>
                      )}
                      {nextAuto && !failed && (
                        <span className="text-[11px] text-[#475569] flex items-center gap-1" data-testid={`auto-echeance-${a.id}`}>
                          <Repeat className="w-3 h-3" /> Prochaine échéance prélevée automatiquement le {new Date(nextAuto.due).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                      {rs.map((r) => (
                        <button key={r.id} onClick={() => downloadRecu(r.id, r.numero_recu)} className="text-[11px] font-bold text-blue-600 hover:underline flex items-center gap-1" data-testid={`recu-${r.id}`}>
                          <FileText className="w-3 h-3" /> Reçu {r.numero_recu}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {selectedFormule && (
        <SouscriptionModal
          formule={selectedFormule}
          tarif={tarif[selectedFormule]}
          onClose={() => setSelectedFormule(null)}
          onSuccess={() => { setSelectedFormule(null); load(); }}
        />
      )}
    </div>
  );
}

function FormuleCard({ icon: Ic, title, subtitle, prix_cts, count, perks, highlighted, disabled, onClick, testid }) {
  return (
    <div
      className={`card relative ${highlighted ? '!border-2' : ''} ${disabled ? 'opacity-50' : ''}`}
      style={highlighted ? { borderColor: 'var(--theme-primary)' } : {}}
      data-testid={testid}
    >
      {highlighted && (
        <div className="absolute -top-2 left-3 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider text-white" style={{ background: 'var(--theme-primary)' }}>
          Recommandé
        </div>
      )}
      <div className="w-9 h-9 rounded-lg grid place-items-center mb-2" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
        <Ic className="w-4 h-4" />
      </div>
      <h3 className="font-display text-base font-black text-[#0F172A]">{title}</h3>
      <div className="font-display text-2xl font-black my-1" style={{ color: 'var(--theme-primary)' }}>
        {prix_cts === 0 ? 'Gratuit' : `${(prix_cts / 100).toFixed(0)} €`}
        {count && <span className="text-xs text-[#475569] font-normal"> × {count}</span>}
      </div>
      <div className="text-[10px] text-[#475569] mb-3">{subtitle}</div>
      <ul className="space-y-1 mb-3">
        {perks.map((p) => (
          <li key={p} className="flex items-start gap-1 text-[11px] text-[#0F172A]">
            <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" style={{ color: 'var(--theme-primary)' }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        disabled={disabled}
        className="w-full text-xs font-bold py-2 rounded-lg text-white disabled:bg-slate-200 disabled:text-[#94A3B8]"
        style={!disabled ? { background: 'var(--theme-primary)' } : {}}
        data-testid={`${testid}-cta`}
      >
        {disabled ? 'Déjà utilisé' : 'Choisir'}
      </button>
    </div>
  );
}

function SouscriptionModal({ formule, tarif, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const isFree = tarif?.prix_cts === 0;

  const subscribe = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/adhesions-ref/souscrire', { formule });
      if (data.statut === 'active') {
        toast.success('Adhésion activée !');
      } else {
        toast.success('Demande envoyée — en attente de validation du bureau');
      }
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.error || 'Erreur');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in" onClick={onClose}>
      <div className="card max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="souscription-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold text-[#0F172A]">Confirmer l&apos;adhésion</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#0F172A] mb-3">
          Vous êtes sur le point de souscrire à la formule <strong>{tarif?.label}</strong> pour{' '}
          <strong>{(tarif?.prix_cts / 100).toFixed(2)} €</strong>{tarif?.count ? ` × ${tarif.count}` : ''}.
        </p>
        {isFree ? (
          <p className="text-xs text-[#475569] mb-4">Cette formule est gratuite et sera activée immédiatement.</p>
        ) : (
          <p className="text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 mb-4">
            Votre demande sera envoyée au bureau. Après validation, vous pourrez régler votre adhésion (carte, chèque ou espèces).
          </p>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-xs">Annuler</button>
          <button onClick={subscribe} disabled={submitting} className="btn-primary text-xs" data-testid="souscription-confirm">
            {submitting ? 'Patientez…' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}
